import { Card, faDigits } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { api, type UnitTrendRow } from "../../api.js";

interface MealRow {
  id: string;
  name: string;
}

function MiniSparkline({ prior, recent }: { prior: number; recent: number }) {
  const max = Math.max(prior, recent, 1);
  const pHeight = Math.max(Math.round((prior / max) * 20), 2);
  const rHeight = Math.max(Math.round((recent / max) * 20), 2);
  const isUp = recent >= prior;

  return (
    <svg
      width="54"
      height="26"
      viewBox="0 0 54 26"
      style={{ verticalAlign: "middle" }}
      aria-label={`نمودار روند: قبل ${String(prior)}، اخیر ${String(recent)}`}
    >
      <line x1="2" y1="24" x2="52" y2="24" stroke="var(--slate-border, #cbd5e1)" strokeWidth="1" />
      {/* ۴ هفته قبلی */}
      <rect x="6" y={24 - pHeight} width="16" height={pHeight} fill="var(--slate-border, #94a3b8)" rx="2" />
      {/* ۴ هفته اخیر */}
      <rect
        x="30"
        y={24 - rHeight}
        width="16"
        height={rHeight}
        fill={isUp ? "var(--teal-ink, #0d9488)" : "var(--coral-ink, #e11d48)"}
        rx="2"
      />
    </svg>
  );
}

export function TrendsTab() {
  const [mealId, setMealId] = useState("");

  const meals = useQuery({
    queryKey: ["menu-lists", "meals"],
    queryFn: () => api<MealRow[]>("/menu/lists/meals").catch(() => []),
  });

  const queryParams = new URLSearchParams();
  if (mealId) queryParams.set("mealId", mealId);

  const trends = useQuery({
    queryKey: ["forecast", "trends", mealId],
    queryFn: () => api<UnitTrendRow[]>(`/forecast/trends?${queryParams.toString()}`),
  });

  const list = trends.data ?? [];

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Filter */}
      <div className="row wrap" style={{ gap: "var(--s3)", alignItems: "flex-end" }}>
        <div className="field">
          <label htmlFor="trend-meal">وعده غذایی</label>
          <select
            id="trend-meal"
            className="input"
            value={mealId}
            onChange={(e) => {
              setMealId(e.target.value);
            }}
          >
            <option value="">پیش‌فرض سیستم</option>
            {(meals.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {trends.isLoading && <p className="ink2">در حال بارگذاری روندهای مصرف واحدها...</p>}
      {trends.error && (
        <p className="hint" role="alert" style={{ color: "var(--coral-ink)" }}>
          خطا در دریافت روندها: {trends.error.message}
        </p>
      )}

      {/* Trends Table */}
      <Card>
        <table className="table" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th>سازمان و واحد</th>
              <th>میانگین ۴ هفته قبل</th>
              <th>میانگین ۴ هفته اخیر</th>
              <th>روند تغییرات</th>
              <th>نمودار تغییرات</th>
              <th>وضعیت سابقه</th>
            </tr>
          </thead>
          <tbody>
            {!trends.isLoading && list.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", color: "var(--muted)" }}>
                  داده‌ای برای تحلیل روند واحدها یافت نشد.
                </td>
              </tr>
            ) : (
              list.map((row) => {
                const trend = row.trendPerMille;
                let trendBadge: ReactNode;

                if (trend === null || row.sampleCountPrior === 0) {
                  trendBadge = <span className="ink2">سابقه ناکافی</span>;
                } else if (trend > 0) {
                  trendBadge = (
                    <span style={{ color: "var(--teal-ink, #0d9488)", fontWeight: "bold" }}>
                      ↑ +{faDigits((trend / 10).toFixed(1))}%
                    </span>
                  );
                } else if (trend < 0) {
                  trendBadge = (
                    <span style={{ color: "var(--coral-ink, #e11d48)", fontWeight: "bold" }}>
                      ↓ {faDigits((trend / 10).toFixed(1))}%
                    </span>
                  );
                } else {
                  trendBadge = <span className="ink2">بدون تغییر (۰٪)</span>;
                }

                return (
                  <tr key={row.unitId}>
                    <td>
                      <strong>
                        {row.organizationName
                          ? `${row.organizationName} — ${row.unitName ?? row.unitId}`
                          : (row.unitName ?? row.unitId)}
                      </strong>
                    </td>
                    <td className="num">{faDigits(row.prior4WeeksPortions)} پرس</td>
                    <td className="num b">{faDigits(row.recent4WeeksPortions)} پرس</td>
                    <td>{trendBadge}</td>
                    <td>
                      <MiniSparkline prior={row.prior4WeeksPortions} recent={row.recent4WeeksPortions} />
                    </td>
                    <td className="hint ink2">
                      {faDigits(row.sampleCountRecent)} هفته اخیر / {faDigits(row.sampleCountPrior)} هفته قبل
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
